import { useTranslation } from "react-i18next"
import { Info, Warning } from "@phosphor-icons/react"
import { TooltipContent, TooltipPortal, TooltipTrigger } from "@radix-ui/react-tooltip"
import { Card, CardBody, CardBodyContent } from "@/components/ui/card"
import { TooltipCustom } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { legacyQmsQuestionnaireByIdQuery$data } from "@/modules/patients/detail/progress-note/components/questionnaires/__generated__/legacyQmsQuestionnaireByIdQuery.graphql"
import { QmsSummaryCriticality } from "@/types/__generated__/union-types"

type QmsQuestionnaireAnswer = NonNullable<
  legacyQmsQuestionnaireByIdQuery$data["qmsQuestionnaireSummaryById"]["questionnaire"]
>["answers"][0]

const LegacyQmsAnswer = ({ item }: { item: QmsQuestionnaireAnswer }) => {
  if (item.shortQuestion === "Disclaimer") {
    return null
  }

  if (!item.clinicalDecisionSupport || item.clinicalDecisionSupport.length === 0) {
    return <NormalAnswer item={item} />
  }

  return <CriticalityAnswer item={item} />
}

const CriticalityAnswer = ({ item }: { item: QmsQuestionnaireAnswer }) => {
  const criticalityPriority = (criticality: QmsSummaryCriticality) => {
    switch (criticality) {
      case "CRITICAL":
        return 1
      case "WARNING":
        return 2
      case "INFO":
        return 3
      case "REVIEW":
        return 4
    }
  }

  const sortedClinicalDecisionSupport = Array.from(item.clinicalDecisionSupport).sort((a, b) => {
    return criticalityPriority(a.criticality) - criticalityPriority(b.criticality)
  })

  const [{ criticality, recommendation }] = sortedClinicalDecisionSupport

  const getCriticalityAnswerType = (criticality: QmsSummaryCriticality) => {
    switch (criticality) {
      case "CRITICAL":
        return {
          backgroundClassName: "bg-critical-surface",
          answerClassName: "text-on-critical-surface",
          iconCriticalityAnswer: <Warning weight="fill" className="text-on-critical-surface" />,
        }
      case "WARNING":
        return {
          backgroundClassName: "bg-warning-surface-subdued",
          answerClassName: "text-on-warning-surface",
          iconCriticalityAnswer: <Warning className="text-on-warning-surface" />,
        }
      case "INFO":
        return {
          backgroundClassName: "bg-surface-subdued",
          answerClassName: "text-on-surface-subdued",
          iconCriticalityAnswer: <Info className="text-on-surface-subdued" />,
        }
      case "REVIEW":
        return {
          backgroundClassName: "",
          answerClassName: "",
          iconCriticalityAnswer: null,
        }
    }
  }

  const { backgroundClassName, answerClassName, iconCriticalityAnswer } = getCriticalityAnswerType(criticality)

  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <div
          className={`grid grid-cols-[24px_minmax(10px,1fr)_minmax(10px,1fr)] gap-2 rounded-lg p-2 ${backgroundClassName}`}>
          {iconCriticalityAnswer}
          <div className={cn("pt-0.5 text-xs wrap-break-word", answerClassName)}>
            {item.shortQuestion || item.question}
          </div>
          <div className="flex flex-col">
            <Answers answers={item.answer?.text ?? ""} className={answerClassName} />
            <Answers type="recommendation" answers={recommendation} className={answerClassName} />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="center" className="py-2">
          {(item.missedAnswers.length > 0 || item.shortQuestion) && (
            <Card className="bg-surface-inverted border-r-0">
              <CardBody>
                <CardBodyContent className="text-on-surface-inverted mt-3 inline-block max-w-72 text-sm">
                  {item.question && <p>{item.question}</p>}
                  <ul className="ml-8 list-disc">
                    {item.missedAnswers.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                    {item.answer?.text && item.answer.text.split("|").map((answer) => <li key={answer}>{answer}</li>)}
                  </ul>
                </CardBodyContent>
              </CardBody>
            </Card>
          )}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

const NormalAnswer = ({ item }: { item: QmsQuestionnaireAnswer }) => {
  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <div className="grid grid-cols-[minmax(10px,1fr)_minmax(10px,1fr)] gap-2 p-2 pl-10">
          <div className="pt-0.5 text-xs wrap-break-word">{item.shortQuestion || item.question}</div>
          <div className="flex flex-col">
            <Answers answers={item.answer?.text ?? ""} />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="center" className="py-2">
          {(item.missedAnswers.length > 0 || item.shortQuestion) && (
            <Card className="bg-surface-inverted border-r-0">
              <CardBody>
                <CardBodyContent className="text-on-surface-inverted mt-3 inline-block max-w-72 text-sm">
                  {item.question && <p>{item.question}</p>}
                  <ul className="ml-8 list-disc">
                    {item.missedAnswers.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                    {item.answer?.text && item.answer.text.split("|").map((answer) => <li key={answer}>{answer}</li>)}
                  </ul>
                </CardBodyContent>
              </CardBody>
            </Card>
          )}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

const Answers = ({
  answers,
  className,
  type = "normal",
}: {
  answers: string
  className?: string
  type?: "recommendation" | "normal"
}) => {
  const { t } = useTranslation()
  if (!answers) return null
  const answerTexts = answers.split("|")

  switch (type) {
    case "recommendation": {
      return (
        <div className={cn("text-on-surface text-sm leading-5 font-medium", className)}>
          <p>{`${t("patient.questionnaire.hint")}: `}</p>
          {answerTexts.map((text) => (
            <p key={text}>{text}</p>
          ))}
        </div>
      )
    }
    case "normal": {
      return answerTexts.map((text) => (
        <p className={cn("text-on-surface text-sm leading-5", className)} key={text}>
          {text}
        </p>
      ))
    }
  }
}

export { LegacyQmsAnswer }
